import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, Breadcrumbs, Link, Typography } from "@mui/material";
import { observer } from "mobx-react";
import theme from "Theme/AppTheme";
import type ChartCardStore from "./ChartCardStore";

/**
 * Breadcrumb trail rendered above the chart canvas when the user has zoomed
 * in. Independent of chart type — any chart card whose ZoomStore is currently
 * zoomed shows breadcrumbs. The leading crumb is the top-level label from
 * vizOptions (the chart's first level label, e.g. "Themes"), followed by one
 * crumb per zoom-path entry. Clicking crumb `i` calls
 * `zoomStore.navigateToDepth(i)`; the leading crumb pops back to the top
 * level (depth 0).
 */
const ZoomBreadcrumbs = observer(
    ({ store }: { store: ChartCardStore }) => {
        const zoomStore = store.zoomStore;
        if (!zoomStore?.isZoomed) return null;

        const levelLabels = store.parsedVizOptions?.levelLabels ?? [];
        const topLevelLabel = levelLabels[0] ?? store.xTitle ?? "All";

        // Each zoom entry's display label becomes a crumb after the top level.
        const items: { label: string; depth: number }[] = [
            { label: topLevelLabel, depth: 0 },
            ...zoomStore.zoomPath.map((entry, i) => ({
                label: entry.x,
                depth: i + 1,
            })),
        ];

        const crumbColor = "#9CA3AF";
        const leafColor = "#4B5563";
        const baseSx = {
            alignItems: "center",
            display: "inline-flex",
            fontFamily: theme.typography.fontFamily,
            fontSize: "14px",
            fontWeight: theme.typography.fontWeightSemibold,
            height: "20px",
            lineHeight: "20px",
        } as const;

        return (
            <Box
                sx={{
                    width: "100%",
                    py: theme.spacing(1),
                }}
            >
                <Breadcrumbs
                    separator={
                        <ChevronRightIcon
                            sx={{
                                color: crumbColor,
                                display: "block",
                                fontSize: "18px",
                                width: "18px",
                                height: "18px",
                            }}
                        />
                    }
                    sx={{
                        "& .MuiBreadcrumbs-separator": {
                            alignItems: "center",
                            display: "flex",
                            height: "20px",
                            lineHeight: 0,
                            mx: "3px",
                        },
                    }}
                >
                    {items.map((item, idx) => {
                        const isLast = idx === items.length - 1;
                        if (isLast) {
                            return (
                                <Typography
                                    key={`${item.label}-${idx}`}
                                    sx={{ ...baseSx, color: leafColor }}
                                >
                                    {item.label}
                                </Typography>
                            );
                        }
                        return (
                            <Link
                                key={`${item.label}-${idx}`}
                                component="button"
                                underline="hover"
                                onClick={() => {
                                    void zoomStore.navigateToDepth(item.depth);
                                }}
                                sx={{
                                    ...baseSx,
                                    color: crumbColor,
                                    background: "none",
                                    border: "none",
                                    p: 0,
                                    cursor: "pointer",
                                }}
                            >
                                {item.label}
                            </Link>
                        );
                    })}
                </Breadcrumbs>
            </Box>
        );
    },
);

export default ZoomBreadcrumbs;
